// ============================================================================
// CHANGO V5.0 — D5.4 Client-side Telemetry (Sentry Browser + posthog-js)
// ============================================================================
// Mirrors server/lib/telemetry.js one-to-one so the 12-event taxonomy lines up
// across both sides of the wire. Design rules:
//
//   * Both SDKs are IMPORTED LAZILY — a VITE_*_DSN / VITE_POSTHOG_KEY env var
//     that is blank or missing puts the module into no-op mode. Safe for dev,
//     CI previews, and air-gapped demos.
//   * We do NOT bundle @sentry/browser or posthog-js eagerly; they're pulled
//     in on-demand via dynamic import() the first time initTelemetry() fires.
//     Vite's manualChunks config splits them into their own async chunk.
//   * `identify()` is called AFTER login resolves so anonymous pre-login traffic
//     still gets a distinct_id (auto-generated by posthog-js).
//   * `captureError()` wraps Sentry.captureException with a structured tag bag.
//     It never throws — telemetry must never break the app.
//   * §20 #17 reminder: never pass request bodies / costs / profits into
//     telemetry properties. Call sites that emit `quote.submitted` should send
//     the id + workspaceId + lifecycle_status only.
// ============================================================================

// --- Event taxonomy (must match server/lib/telemetry.js) -------------------
export const TELEMETRY_EVENTS = Object.freeze([
  'auth.login_ok',
  'auth.login_failed',
  'auth.register_completed',
  'quote.created',
  'quote.submitted',
  'quote.approved',
  'quote.rejected',
  'share.created',
  'share.viewed',
  'billing.checkout_started',
  'billing.payment_completed',
  'billing.refund_requested',
]);

// --- Vite env surface -------------------------------------------------------
// Vite inlines import.meta.env.* at build-time, so these reads happen once on
// module load. Falsy values (including undefined) → module stays inert.
const SENTRY_DSN =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SENTRY_DSN) || '';
const SENTRY_ENV =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SENTRY_ENV) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.MODE) ||
  'development';
const SENTRY_TRACES =
  Number(
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.1
  ) || 0.1;
const POSTHOG_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_POSTHOG_KEY) || '';
const POSTHOG_HOST =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_POSTHOG_HOST) ||
  'https://us.i.posthog.com';

// --- Lazy module refs -------------------------------------------------------
let _sentry = null;   // resolved @sentry/browser module
let _posthog = null;  // resolved posthog-js client
let _initPromise = null;

/**
 * One-shot bootstrap. Call once from main.jsx *before* rendering <App/>. Safe
 * to call multiple times — the first call wins and the promise is cached.
 *
 * Returns a promise that resolves with { sentryReady, posthogReady } so tests
 * can assert the state without touching module internals.
 */
export function initTelemetry() {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    if (SENTRY_DSN) {
      try {
        const Sentry = await import('@sentry/browser');
        Sentry.init({
          dsn: SENTRY_DSN,
          environment: SENTRY_ENV,
          tracesSampleRate: SENTRY_TRACES,
          // sendDefaultPii defaults to false; we repeat here for reviewability.
          sendDefaultPii: false,
          // Ignore ResizeObserver loop + "Script error" noise that dominates
          // web error dashboards without ever helping diagnose real bugs.
          ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications.',
            'Script error.',
          ],
        });
        _sentry = Sentry;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[telemetry] sentry init failed — continuing without', err);
      }
    }

    if (POSTHOG_KEY) {
      try {
        const mod = await import('posthog-js');
        const posthog = mod.default || mod;
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          // Privacy-first defaults:
          //   * session recordings off (we haven't acquired the user consent UX)
          //   * autocapture off (we emit events explicitly from the 12 taxonomy)
          //   * cross_subdomain_cookie scoped to our apex so app./admin./ops.
          //     all see the same distinct_id without leaking elsewhere.
          disable_session_recording: true,
          autocapture: false,
          persistence: 'localStorage+cookie',
          cross_subdomain_cookie: true,
        });
        _posthog = posthog;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[telemetry] posthog init failed — continuing without', err);
      }
    }

    return {
      sentryReady: !!_sentry,
      posthogReady: !!_posthog,
    };
  })();
  return _initPromise;
}

/**
 * Bind the current session's user to the telemetry channels. Safe to call
 * multiple times (e.g., after login and after refresh token swap).
 *
 *   distinctId  stable user id — usually the numeric users.id cast to string
 *   traits      { role, workspaceId, uiMode } — NEVER include email/phone/name
 */
export function identify(distinctId, traits = {}) {
  const id = distinctId == null ? null : String(distinctId);
  if (!id) return;
  try {
    if (_posthog) {
      _posthog.identify(id, traits);
    }
  } catch { /* telemetry never throws */ }
  try {
    if (_sentry) {
      // Don't pass email/name — we deliberately keep the user blob role-only.
      _sentry.setUser({ id, ...traits });
    }
  } catch { /* noop */ }
}

/**
 * Reset the distinct_id on logout so the next session gets a fresh anonymous
 * id rather than inheriting the previous user's.
 */
export function resetIdentity() {
  try { if (_posthog) _posthog.reset(); } catch { /* noop */ }
  try { if (_sentry) _sentry.setUser(null); } catch { /* noop */ }
}

/**
 * Emit a product event. The event name MUST be in TELEMETRY_EVENTS or we warn
 * to console (build-time ESLint rule catches drift too).
 *
 *   event        dotted event name from TELEMETRY_EVENTS
 *   properties   structured metadata — NO cost/profit/margin fields (§20 #17)
 */
export function trackEvent(event, properties = {}) {
  if (typeof event !== 'string' || !event.includes('.')) {
    // eslint-disable-next-line no-console
    console.warn('[telemetry] trackEvent: event must be a dotted name', event);
    return;
  }
  if (!TELEMETRY_EVENTS.includes(event)) {
    // eslint-disable-next-line no-console
    console.warn('[telemetry] trackEvent: unknown event name (not in TELEMETRY_EVENTS)', event);
  }
  // Belt-and-braces: scrub any obviously-sensitive property keys before send.
  const safeProps = scrubProps(properties);
  try {
    if (_posthog) {
      _posthog.capture(event, safeProps);
    }
  } catch { /* noop */ }
}

/**
 * Forward a caught Error to Sentry with optional structured context. Same
 * rules as server-side: never break the caller.
 */
export function captureError(err, context = {}) {
  try {
    if (!_sentry) return;
    _sentry.withScope((scope) => {
      for (const [k, v] of Object.entries(context)) {
        if (v == null) continue;
        scope.setTag(k, String(v));
      }
      _sentry.captureException(err);
    });
  } catch { /* noop */ }
}

/**
 * Wrap an async function so any thrown error is captured, then re-thrown so
 * React error boundaries still see it. Useful for event handlers that would
 * otherwise swallow promise rejections.
 */
export function instrument(fn, context = {}) {
  return async function instrumented(...args) {
    try {
      return await fn.apply(this, args);
    } catch (err) {
      captureError(err, context);
      throw err;
    }
  };
}

// --- Internal helpers -------------------------------------------------------

const SENSITIVE_KEY_RE = /^(total|cost|profit|margin|gross_profit|unit_cost|password|token)/i;

function scrubProps(props) {
  if (!props || typeof props !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (SENSITIVE_KEY_RE.test(k)) continue;
    // Soft cap string length so we don't accidentally inflate events.
    if (typeof v === 'string' && v.length > 500) {
      out[k] = v.slice(0, 500);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Introspection for tests. */
export function _internalState() {
  return {
    sentryReady: !!_sentry,
    posthogReady: !!_posthog,
    hasInit: !!_initPromise,
    sentryDsnConfigured: !!SENTRY_DSN,
    posthogKeyConfigured: !!POSTHOG_KEY,
  };
}
